import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { AppShell } from '../components/AppShell';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { AccountDialog, SecurityDialog, NotificationsDialog } from '../components/profil/ProfileDialogs';
import { 
  User,
  Envelope,
  SignOut,
  Gear,
  Bell,
  ShieldCheck,
  Buildings,
  ArrowLeft
} from '@phosphor-icons/react';

export function ProfilPage() {
  const { user, logout, setUser } = useAuth();
  const navigate = useNavigate();
  const [openDialog, setOpenDialog] = useState(null); // 'account' | 'security' | 'notifications' | null

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <AppShell>
      <div className="space-y-6 max-w-2xl mx-auto" data-testid="profil-page">
        {/* Back button + Profile Card */}
        <div className="flex items-start gap-3">
          <Button variant="ghost" size="icon" className="h-10 w-10 md:hidden mt-4"
            onClick={() => navigate('/dashboard')} data-testid="back-btn">
            <ArrowLeft size={22} />
          </Button>
          <Card className="chantix-card flex-1">
          <CardContent className="p-6">
            <div className="flex flex-col items-center text-center">
              <Avatar className="w-24 h-24 mb-4">
                {user?.picture ? (
                  <AvatarImage src={user.picture} alt={user.name} />
                ) : null}
                <AvatarFallback className="bg-[#0055FF] text-white text-2xl">
                  {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                </AvatarFallback>
              </Avatar>
              <h1 className="text-2xl font-bold text-[#111827]" data-testid="profile-name">
                {user?.name || 'Utilisateur'}
              </h1>
              <p className="text-[#4B5563] flex items-center gap-2 mt-1">
                <Envelope size={16} />
                {user?.email}
              </p>
              <span className="chantix-badge chantix-badge-info mt-2">
                {user?.role === 'admin' ? 'Administrateur' : 'Utilisateur'}
              </span>
            </div>
          </CardContent>
        </Card>
        </div>

        {/* Settings */}
        <Card className="chantix-card">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Gear size={20} />
              Paramètres
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <Button
              variant="ghost"
              className="w-full justify-start h-auto py-3"
              onClick={() => navigate('/settings')}
              data-testid="company-settings-btn"
            >
              <Buildings size={20} className="mr-3 text-[#0055FF]" />
              <div className="text-left">
                <p className="font-medium">Paramètres entreprise</p>
                <p className="text-sm text-[#4B5563]">Logo, coordonnées et signature</p>
              </div>
            </Button>
            <Button
              variant="ghost"
              className="w-full justify-start h-auto py-3"
              onClick={() => setOpenDialog('notifications')}
              data-testid="notifications-settings"
            >
              <Bell size={20} className="mr-3 text-[#4B5563]" />
              <div className="text-left">
                <p className="font-medium">Notifications</p>
                <p className="text-sm text-[#4B5563]">Gérer les notifications</p>
              </div>
            </Button>
            <Button
              variant="ghost"
              className="w-full justify-start h-auto py-3"
              onClick={() => setOpenDialog('security')}
              data-testid="security-settings"
            >
              <ShieldCheck size={20} className="mr-3 text-[#4B5563]" />
              <div className="text-left">
                <p className="font-medium">Sécurité</p>
                <p className="text-sm text-[#4B5563]">Mot de passe et authentification</p>
              </div>
            </Button>
            <Button
              variant="ghost"
              className="w-full justify-start h-auto py-3"
              onClick={() => setOpenDialog('account')}
              data-testid="account-settings"
            >
              <User size={20} className="mr-3 text-[#4B5563]" />
              <div className="text-left">
                <p className="font-medium">Mon compte</p>
                <p className="text-sm text-[#4B5563]">Informations personnelles</p>
              </div>
            </Button>
          </CardContent>
        </Card>

        {/* Logout */}
        <Button
          variant="outline"
          onClick={handleLogout}
          className="w-full border-red-300 text-red-600 hover:bg-red-50"
          data-testid="logout-btn-profile"
        >
          <SignOut size={20} className="mr-2" />
          Se déconnecter
        </Button>

        {/* App Info */}
        <div className="text-center text-sm text-[#4B5563]">
          <p>CHANTIX v1.0.0</p>
          <p>© 2024 - Gestion de chantier BTP</p>
        </div>
      </div>

      <AccountDialog
        open={openDialog === 'account'}
        onClose={() => setOpenDialog(null)}
        user={user}
        onUpdated={(updated) => { if (setUser) setUser(updated); }}
      />
      <SecurityDialog
        open={openDialog === 'security'}
        onClose={() => setOpenDialog(null)}
      />
      <NotificationsDialog
        open={openDialog === 'notifications'}
        onClose={() => setOpenDialog(null)}
      />
    </AppShell>
  );
}
